import maybe from 'folktale/maybe'
import result from 'folktale/result'
import {
  openGenericConfirmationPanel,
  openMultilingualDashboard,
} from '@wix/wix-data-client-common'
import openContentManager from '../panels/openContentManager'
import sequence from '@wix/wix-data-client-dbsm-common/src/fp/sequence'
import externalDbDriverPanelOpener from '../panels/openExternalDbDriverPanel'
import openRemoveExternalDBDriverPanel from '../panels/openRemoveExternalDBDriverPanel'
import openMisconfiguredConnectorPanel from '../panels/openMisconfiguredConnectorPanel'
import openRemoveCollectionPanel from '../panels/openRemoveCollectionPanel'
import openDuplicateCollectionPanel from '../panels/openDuplicateCollectionPanel'
import openDatabaseHooksPanel from '../panels/openDatabaseHooksPanel'

const getPayloadProp = (eventPayload, prop) =>
  maybe.fromNullable(eventPayload && eventPayload[prop])

export default async ({
  appApi,
  hostname,
  logger,
  experimentsManager,
  editorSdkProxy,
  i18n,
  collectionsApi,
  biCMLogger,
  wixDataConnectorSettingsClient,
  wixData,
}) => {
  const openExternalDbDriverPanel = externalDbDriverPanelOpener({
    hostname,
    logger,
    editorSdkProxy,
    i18n,
    experimentsManager,
  })

  const isCollectionFake = collectionName => /\/__error__$/.test(collectionName)

  const t = i18n.t.bind(i18n)

  const openErrorPanel = () =>
    openGenericConfirmationPanel({
      editorSdkProxy,
      logger,
      t,
    })

  return async function ({ eventType, eventPayload }) {
    const getProp = prop => getPayloadProp(eventPayload, prop)
    const getPropResult = prop =>
      getPayloadProp(eventPayload, prop).matchWith({
        Nothing: () =>
          result.Error(`[${eventType}] eventPayload is missing ${prop}`),
        Just: ({ value }) => result.Ok(value),
      })

    const { origin, namespace } = eventPayload || {}

    switch (eventType) {
      case 'openCollectionClicked': {
        const { collectionId } = eventPayload
        openContentManager({
          hostname,
          editorSdkProxy,
          collectionName: collectionId,
          isModal: true,
          origin: 'collection_actions',
          wixData,
          collectionsApi,
          i18n,
          logger,
          appApi,
        }).catch(error => {
          logger.error(
            new Error('Failed to open content manager', { cause: error }),
          )
        })
        break
      }
      case 'editCollectionClicked': {
        const { collectionId } = eventPayload
        await appApi.openCreateCollectionPanel({ origin, collectionId })
        break
      }
      case 'permissionsAndPrivacyClicked': {
        const { collectionId } = eventPayload
        await appApi.openCreateCollectionPanel({
          origin,
          collectionId,
          initialScreen: 'permissions',
        })
        break
      }
      case 'treeItemSelected':
        sequence(result, [getPropResult('itemName')]).matchWith({
          Error: ({ value: error }) => {
            logger.info(`${error}, not opening the CM Editor`)
            openErrorPanel()
          },
          Ok: async ({ value: [collectionName] }) => {
            if (isCollectionFake(collectionName)) {
              openMisconfiguredConnectorPanel({
                editorSdkProxy,
                openExternalDbDriverPanel: appApi.openExternalDbDriverPanel,
                logger,
                i18n,
                collectionName,
              })
            } else {
              openContentManager({
                hostname,
                editorSdkProxy,
                collectionName,
                isModal: eventPayload.isModal,
                origin: 'IDE_TREE',
                wixData,
                collectionsApi,
                i18n,
                logger,
                appApi,
              }).catch(error => {
                logger.error(
                  new Error('Failed to open content manager', { cause: error }),
                )
              })
            }
          },
        })
        break

      case 'openCollectionPermissionsClicked':
        sequence(result, [getPropResult('collectionId')]).matchWith({
          Error: ({ value: error }) => {
            logger.info(`${error}, not opening collection permissions`)
            openErrorPanel()
          },
          Ok: ({ value: [collectionId] }) => {
            appApi.openCollectionPermissionsPanel({
              collectionId,
            })
          },
        })
        break
      case 'openCreateNewCollection': {
        const clickedInSiteStructure = getProp(
          'clickedInSiteStructure',
        ).getOrElse(false)

        await appApi.openCreateCollectionPanel({
          origin,
          clickedInSiteStructure,
          namespace,
        })
        break
      }
      case 'databasePanelCollectionSelected':
        sequence(result, [
          getPropResult('collectionName'),
          getPropResult('origin'),
        ]).matchWith({
          Error: ({ value: error }) => {
            logger.info(`${error}, not opening the CM Editor`)
            openErrorPanel()
          },
          Ok: async ({ value: [collectionName, origin] }) => {
            openContentManager({
              hostname,
              editorSdkProxy,
              collectionName,
              isModal: true,
              origin,
              wixData,
              collectionsApi,
              i18n,
              logger,
              appApi,
            }).catch(error => {
              logger.error(
                new Error('Failed to open content manager', { cause: error }),
              )
            })
          },
        })
        break
      case 'openContentManagerCollectionsList': {
        const { collectionsGroup } = eventPayload
        await appApi.openAutoCmsCollectionsList({ origin, collectionsGroup })
        break
      }
      case 'openContentManagerAdvancedSettings':
        await appApi.openAutoCmsAdvancedSettings({ origin })
        break
      case 'removeCollectionClicked':
        sequence(result, [getPropResult('collectionId')]).matchWith({
          Error: ({ value: error }) => {
            logger.info(`${error}, not opening remove collection panel`)
            openErrorPanel()
          },
          Ok: ({ value: [collectionId] }) => {
            openRemoveCollectionPanel({
              editorSdkProxy,
              collectionsApi,
              appApi,
              logger,
              i18n,
              biCMLogger,
              collectionId,
              hostname,
              origin,
            })
          },
        })
        break
      case 'duplicateCollectionClicked':
        sequence(result, [getPropResult('collectionId')]).matchWith({
          Error: ({ value: error }) => {
            logger.info(`${error}, not opening duplicate collection panel`)
            openErrorPanel()
          },
          Ok: ({ value: [collectionId] }) => {
            openDuplicateCollectionPanel({
              editorSdkProxy,
              collectionId,
              hostname,
              origin,
            })
          },
        })
        break
      case 'removeExternalDbDriverClicked':
        sequence(result, [getPropResult('namespace')]).matchWith({
          Error: ({ value: error }) => {
            logger.info(`${error}, not opening remove external db driver panel`)
            openErrorPanel()
          },
          Ok: ({ value: [namespace] }) => {
            openRemoveExternalDBDriverPanel({
              editorSdkProxy,
              logger,
              appApi,
              wixDataConnectorSettingsClient,
              i18n,
              biCMLogger,
              onError: openErrorPanel,
              namespace,
              experimentsManager,
            })
          },
        })
        break
      case 'addRemoveHooksClicked':
        sequence(result, [getPropResult('collectionId')]).matchWith({
          Error: ({ value: error }) => {
            logger.info(`${error}, not opening hooks panel`)
            openErrorPanel()
          },
          Ok: async ({ value: [collectionId] }) => {
            try {
              await openDatabaseHooksPanel({
                hostname,
                editorSdkProxy,
                i18n,
                collectionId,
              })
            } catch (err) {
              logger.error(
                new Error('Failed to open database hooks panel', {
                  cause: err,
                }),
              )
            }
          },
        })
        break
      case 'addExternalDbDriverClicked':
        openExternalDbDriverPanel()
        break
      case 'editExternalDbDriverClicked':
        openExternalDbDriverPanel(eventPayload.namespace, true)
        break
      case 'manageTranslations':
        openMultilingualDashboard({ editorSdkProxy, logger })
        break
      default:
        // TODO log something here?
        break
    }
  }
}
